import { useCallback, useEffect, useState } from 'react';

import { PersonContract } from '~/data/employeeProfile';
import { useAllEnums } from '~/data/enums';
import { useAllOrgUnits } from '~/data/orgunits';
import { OrganizationUnit } from '~/data/orgunits/types';

type PropsType = {
  contracts: PersonContract[];
};

type OutputProps = {
  overlappingEmployments: OverlappingEmployment[];
  isLoading: boolean;
};

export type OverlappingEmployment = PersonContract & {
  unitName?: string;
  unitNumber?: number;
  unitPosition?: string;
};

export const useOverlappingEmployments = (props: PropsType): OutputProps => {
  const { contracts } = props;
  const { orgUnits: allOrgUnits } = useAllOrgUnits();
  const {
    allEnums,
    isLoading: isLoadingEnums,
    failedToLoad: failedToLoadEnums,
  } = useAllEnums();
  const isLoading = allOrgUnits.length <= 0;
  const [employments, setEmployments] = useState<OverlappingEmployment[]>([]);

  const resolveEmployments = useCallback(
    (contracts: PersonContract[], orgUnits: OrganizationUnit[]) => {
      const getOrgUnitValues = (orgUnit: OrganizationUnit) => {
        return {
          unitName: orgUnit.fOrgUnitName,
          unitNumber: parseInt(orgUnit.fOrgUnitNumber),
        };
      };

      const getUnitPosition = (code: string) => {
        if (isLoadingEnums || failedToLoadEnums) {
          return code;
        }
        const posEnum = allEnums.EMPLOYEE_POSITION
          ? allEnums.EMPLOYEE_POSITION.find((p) => p.code === code)
          : null;
        return posEnum ? posEnum.name : code;
      };

      const employments = !!contracts
        ? contracts.map((contract) => {
            const orgUnit = orgUnits.find(
              (u) => u.fOrgUnitId === contract.unit,
            );
            const unitDetails = !!orgUnit ? getOrgUnitValues(orgUnit) : {};
            const unitPosition = getUnitPosition(contract.position);

            return {
              ...contract,
              ...unitDetails,
              unitPosition,
            };
          })
        : [];

      setEmployments(employments);
    },
    [allEnums, isLoadingEnums, failedToLoadEnums],
  );

  useEffect(() => {
    const unitIds = contracts.map((c) => c.unit);
    const orgUnits = allOrgUnits.filter((u) => unitIds.includes(u.fOrgUnitId));
    resolveEmployments(contracts, orgUnits);
  }, [contracts, allOrgUnits, resolveEmployments]);

  return {
    overlappingEmployments: employments,
    isLoading: isLoading,
  };
};
